/**
 * Pure data helpers for the KidHive class detail page. No React, no config, no SDK -- everything the
 * page derives from the listing entity + the already-fetched monthly time slots lives here so the
 * format-aware display (single-session vs term) is unit-testable, and the booking modal later reads
 * the SAME derivations the on-page schedule preview shows.
 */
import { getTermSessions, getTermTimezone, getTermWindow } from '../../../util/termSchedule';
import { isValidTimeZone, parseDateTimeString } from '../../../util/dates';
import { LAUNCH_TIME_ZONE } from '../../../util/launchRegions';

const isPlainObject = value => value != null && typeof value === 'object' && !Array.isArray(value);

const MS_PER_MINUTE = 60 * 1000;

// ================ Time slots (single-session schedule + spots left) ================

/**
 * Flatten the duck's monthlyTimeSlots ({ '2026-07': { timeSlots: [...] } }) into one array of
 * UPCOMING slots sorted by start time. Slots without a usable start, or starting before `now`,
 * are dropped.
 */
export const flattenMonthlyTimeSlots = (monthlyTimeSlots, now = new Date(), notBeforeMs = null) => {
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  // A drop-in class can carry a start date (publicData.scheduleInput.startDate): it isn't bookable
  // before then, so the floor is the later of "now" and that start date. notBeforeMs null => now only.
  const floorMs = Math.max(nowMs, typeof notBeforeMs === 'number' ? notBeforeMs : 0);
  const months = isPlainObject(monthlyTimeSlots) ? Object.values(monthlyTimeSlots) : [];
  return months
    .flatMap(month => (Array.isArray(month?.timeSlots) ? month.timeSlots : []))
    .filter(slot => {
      const start = slot?.attributes?.start;
      return start instanceof Date && !Number.isNaN(start.getTime()) && start.getTime() >= floorMs;
    })
    .sort((a, b) => a.attributes.start - b.attributes.start);
};

/**
 * The bookability FLOOR (epoch ms) for a class that carries a drop-in start date
 * (publicData.scheduleInput.startDate = 'YYYY-MM-DD'): sessions before it aren't offered. Null when
 * no start date is set. UX only -- the server floor at initiate is authoritative. Parsed at the day's
 * start; the marketplace is single-region (LA/OC) so local parsing matches the listing timezone.
 */
export const classBookableFromMs = listing => {
  const startDate = listing?.attributes?.publicData?.scheduleInput?.startDate;
  if (typeof startDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
    return null;
  }
  // Midnight in the CLASS's zone (the server gate in api-util/classStartDate.js uses the same rule),
  // not the browser's: a visitor elsewhere must see the same first bookable session as the server.
  const planZone = listing?.attributes?.availabilityPlan?.timezone;
  const timeZone = planZone && isValidTimeZone(planZone) ? planZone : LAUNCH_TIME_ZONE;
  const ms = parseDateTimeString(`${startDate} 00:00`, timeZone).getTime();
  return Number.isNaN(ms) ? null : ms;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Slots starting within `maxDays` from now -- the advance-booking cap, so no slot beyond the limit is
 * ever offered (the parent can't select a session the checkout would then reject). The cap value is
 * passed IN (from config) so this module stays config-free. `maxDays` <= 0 disables the cap.
 */
export const slotsWithinDays = (slots, maxDays, now = Date.now()) => {
  if (!(maxDays > 0)) {
    return Array.isArray(slots) ? slots : [];
  }
  const capMs = now + maxDays * MS_PER_DAY;
  return (Array.isArray(slots) ? slots : []).filter(slot => {
    const start = slot?.attributes?.start;
    const ms = start instanceof Date ? start.getTime() : new Date(start).getTime();
    return !Number.isNaN(ms) && ms <= capMs;
  });
};

/**
 * Group upcoming slots into per-day buckets (the mockup's date pills), capped at `maxDays` distinct
 * days. The day key is computed in the given IANA timezone so a 7pm PT slot doesn't land on the
 * next day's pill. Returns [{ dayKey: 'YYYY-MM-DD', date, slots }] sorted by day.
 */
export const upcomingSlotDays = (slots, { maxDays = 4, timeZone } = {}) => {
  const dayKeyOf = date => {
    // en-CA yields YYYY-MM-DD; an invalid/missing timezone falls back to the environment's zone.
    try {
      return new Intl.DateTimeFormat('en-CA', timeZone ? { timeZone } : {}).format(date);
    } catch (e) {
      return new Intl.DateTimeFormat('en-CA').format(date);
    }
  };
  const byDay = new Map();
  (Array.isArray(slots) ? slots : []).forEach(slot => {
    const key = dayKeyOf(slot.attributes.start);
    if (!byDay.has(key) && byDay.size >= maxDays) {
      return;
    }
    const bucket = byDay.get(key) || { dayKey: key, date: slot.attributes.start, slots: [] };
    bucket.slots.push(slot);
    byDay.set(key, bucket);
  });
  return Array.from(byDay.values());
};

/** Seats remaining on a slot (null when the API didn't include seats). */
export const slotSeats = slot => {
  const seats = slot?.attributes?.seats;
  return Number.isInteger(seats) && seats >= 0 ? seats : null;
};

/**
 * The next bookable slot with its seat count -- the header's "N spots left this Saturday" line.
 * Null when there are no upcoming slots.
 */
export const nextAvailableSlot = (monthlyTimeSlots, now = new Date(), notBeforeMs = null) => {
  const [first] = flattenMonthlyTimeSlots(monthlyTimeSlots, now, notBeforeMs);
  return first ? { start: first.attributes.start, seats: slotSeats(first) } : null;
};

// ================ Format-aware derivations ================

/** The listing's schedule timezone: availability plan first, then the term schedule. */
export const listingTimeZone = listing =>
  listing?.attributes?.availabilityPlan?.timezone || getTermTimezone(listing) || null;

/**
 * Session length in minutes. Single-session classes save it publicly (scheduleInput.durationMinutes);
 * term classes derive it from the first session's start/end. Null when neither is available.
 */
export const sessionDurationMinutes = listing => {
  const publicData = listing?.attributes?.publicData || {};
  const fromInput = Number(publicData.scheduleInput?.durationMinutes);
  if (Number.isFinite(fromInput) && fromInput > 0) {
    return Math.round(fromInput);
  }
  // getTermSessions returns validated, sorted { start: Date, end: Date } pairs.
  const [firstSession] = getTermSessions(listing);
  if (firstSession) {
    return Math.round((firstSession.end - firstSession.start) / MS_PER_MINUTE);
  }
  return null;
};

/**
 * The term summary for the schedule card: window (first/last session) + session rows (already
 * validated/sorted Dates from getTermSessions). Null for non-term listings or an unscheduled term.
 */
export const termScheduleSummary = listing => {
  const window = getTermWindow(listing);
  if (!window) {
    return null;
  }
  const sessions = getTermSessions(listing);
  return {
    start: window.start,
    end: window.end,
    sessionCount: sessions.length,
    sessions,
  };
};
